import { ExtensionTag, NodeExtension } from '@remirror/core';

export class MentionExtension extends NodeExtension {
  static disableExtraAttributes = true;

  constructor({ activeUser, ...props }) {
    super({ ...props, priority: 100000 });
    this.activeUser = activeUser;
  }

  get name() {
    return 'mention';
  }

  createTags() {
    return [ExtensionTag.InlineNode];
  }

  createNodeSpec() {
    return {
      attrs: {
        id: { default: null },
        name: { default: null },
        avatar: { default: null },
        color: { default: null },
        active: { default: false }, // Add this to track mention active state
      },
      inline: true,
      group: 'inline',
      selectable: true, // Changed to true to allow selection
      atom: true,
      parseDOM: [
        {
          tag: 'span[data-mention="true"]',
          priority: this.priority,
          getAttrs: (dom) => {
            const element = dom;
            return {
              id: element.getAttribute('data-mention-id'),
              name: element.getAttribute('data-mention-name'),
              avatar: element.getAttribute('data-mention-avatar'),
              color: element.getAttribute('data-mention-color'),
              active: element.getAttribute('data-mention-active'),
              mention: element.getAttribute('data-mention') || true,
            };
          },
        },
      ],
      toDOM: (node) => {
        const { id, name, avatar, color, active } = node.attrs;
        const isSelfMention = this.activeUser?.email === id || this.activeUser?.name === name;
        return [
          'span',
          {
            class: `cms-mention-wrapper ${isSelfMention ? 'self_mention' : ''}`,
            'data-mention': 'true',
            'data-mention-id': id,
            'data-mention-name': name,
            'data-mention-avatar': avatar,
            'data-mention-color': color,
            'data-mention-active': active ? 'true' : undefined,
          },
          [
            'span',
            {
              class: 'ant-typography',
              style: `font-family: var(--cms-font-family); font-weight: 500; color: white; font-size: 14px;border-radius: 4px; padding: 2px 4px; background-color: ${isSelfMention ? 'red' : 'var(--cms-color-primary-active)'}`,
            },
            '@' + name,
          ],
        ];
      },
    };
  }

  createCommands() {
    return {
      insertMention: (attrs) => {
        return ({ tr, dispatch }) => {
          if (dispatch) {
            const node = this.type.create({ ...attrs, active: true });
            tr.replaceSelectionWith(node);
          }
          return true;
        };
      },

      setMentionActive: (active) => {
        return ({ tr, state, dispatch }) => {
          const { $from } = state.selection;
          const node = $from.nodeAfter;
          if (!node || node.type.name !== this.name) return false;

          if (dispatch) {
            tr.setNodeMarkup($from.pos, null, {
              ...node.attrs,
              active,
            });
            dispatch(tr);
          }
          return true;
        };
      },

      deleteMention:
        () =>
        ({ state, dispatch }) => {
          const { selection } = state;
          const { $from, empty } = selection;

          if (!empty) return false;

          const before = $from.nodeBefore;
          if (!before || before.type.name !== this.name) return false;

          if (dispatch) {
            const tr = state.tr.delete($from.pos - before.nodeSize, $from.pos);
            dispatch(tr);
          }
          return true;
        },
    };
  }

  createKeymap() {
    return {
      Enter: ({ state, dispatch }) => {
        const { $from } = state.selection;
        const node = $from.nodeAfter;

        if (node && node.type.name === this.name && node.attrs.active) {
          return true;
        }
        return false;
      },

      ArrowDown: ({ state, dispatch }) => {
        const { $from } = state.selection;
        const node = $from.nodeAfter;

        // Add this check to ensure we're actually in a mention context
        if (!node) {
          const nodeBefore = $from.nodeBefore;
          if (nodeBefore && nodeBefore.type.name === this.name && nodeBefore.attrs.active) {
            return true;
          }
          return false;
        }

        if (node.type.name === this.name && node.attrs.active) {
          // You can add your navigation logic here
          if (dispatch) {
            // Handle the arrow down navigation
            // For example, you might want to emit an event or update some state
            this.store.commands.emit('mention-navigate', { direction: 'down' });
          }
          return true;
        }
        return false;
      },

      ArrowUp: ({ state, dispatch }) => {
        const { $from } = state.selection;
        const node = $from.nodeAfter;

        // Similar check as ArrowDown
        if (!node) {
          const nodeBefore = $from.nodeBefore;
          if (nodeBefore && nodeBefore.type.name === this.name && nodeBefore.attrs.active) {
            return true;
          }
          return false;
        }

        if (node.type.name === this.name && node.attrs.active) {
          if (dispatch) {
            this.store.commands.emit('mention-navigate', { direction: 'up' });
          }
          return true;
        }
        return false;
      },

      Escape: ({ state, dispatch }) => {
        if (dispatch) {
          this.store.commands.setMentionActive(false);
        }
        return true;
      },

      Backspace: () => this.store.commands.deleteMention(),
    };
  }
}

export default MentionExtension;
